// Chakra Imports
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  FormControl,
  Input,
  RadioGroup,
  Radio,
  Text,
  Icon
} from "@chakra-ui/react";

import * as mdIcons from "react-icons/md";

// Components
import { CustomAlert } from "components/alerts/CustomAlert";
import FilePicker from "components/filePicker/file-picker";

// API requests
import {
  ApiSetCoverageInputParameterValue,
  ApiMassLoadCoverageParameterFile,

} from "api/coverage/inputParameters";

// Hooks and CTX
import { Formik, FormikProps } from "formik";
import { useState, useRef } from "react";
import { useLoading } from "contexts/LoadingContext";
import { Select, OptionBase, GroupBase } from "chakra-react-select";

export interface CoverageFilterOption extends OptionBase {
  label: string;
  value: string;
}

/** Opciones para filtros de cobertura (misma lógica que FilterInput). */
export function buildCoverageFilterOptions(
  attributeName: string,
  productOptions: Array<Record<string, unknown>>,
): CoverageFilterOption[] {
  let key = attributeName.split("filter_product_")[1];
  let type: "base" | "attr" = "base";
  if (!key) {
    key = attributeName.split("filter_attribute_product_")[1];
    type = "attr";
  }
  const opts: CoverageFilterOption[] = [];
  productOptions.forEach((opt) => {
    let elem: CoverageFilterOption;
    if (type === "base") {
      elem = {
        label: String(opt[key as keyof typeof opt]),
        value: String(opt[key as keyof typeof opt]),
      };
    } else {
      const attrs = opt.attributes as Array<{ name: string; value: string }>;
      const attr = attrs.filter((a) => a.name.toLowerCase() === key)[0];
      if (!attr) {
        return;
      }
      elem = { label: attr.value, value: attr.value };
    }
    if (!opts.some((o) => o.value === elem.value)) {
      opts.push(elem);
    }
  });
  return [{ label: "Todos", value: "all" }].concat(opts);
}

export const CommonInput = (props: {
  nameString: string;
  formikProps: any
}) => {
  const { formikProps, nameString } = props
  return (
    <Input
      isRequired={true}
      variant="auth"
      fontSize="sm"
      ms={{ base: "0px", md: "0px" }}
      type="text"
      placeholder={nameString}
      fontWeight="500"
      mb="8px"
      size="lg"
      name="value"
      onChange={formikProps.handleChange}
    />
  )
}

export const MassLoadInput = (props: {
  name: string;
  nameString: string;
  formikProps: any;
  pickerRef: any;
  hasTemplate: boolean;
}) => {
  const { formikProps, name, nameString, pickerRef, hasTemplate } = props
  return (
    <>
      {name === "products_master" ? <CustomAlert
        status="warning"
        message='Utilizar el modo "Sustituir" causará que tenga que volver a cargar el Forecast.'
        mb="24px"
      /> : null}
      <Box display='flex' justifyContent='space-between' pb='5px'>
        <RadioGroup
          name="mode"
          onChange={(v) => formikProps.values.mode = v}
          w='max-content'
          display='inherit'
        >
          <Text as='span' alignSelf='center'>Modo:</Text>
          <Radio name="mode" value="update" ml="15px">
            Actualizar
          </Radio>
          <Radio name="mode" value="clear" ml="15px">
            Sustituir
          </Radio>
        </RadioGroup>
        {hasTemplate ? (
          <a href={process.env.REACT_APP_TEMPLATE_URL + `${name}/template.xlsx`} download>
            <Button colorScheme="blue" size="sm">
              <Icon as={mdIcons.MdDownloadDone} h="16px" w="16px" me="8px" />
              <Text fontSize="sm">
                Plantilla
              </Text>
            </Button>
          </a>
        ) : null}
      </Box>
      <FilePicker
        inputProps={{
          "aria-required": true,
          size: "lg",
          mb: "8px",
          fontWeight: "500",
          variant: "auth",
          fontSize: "sm",
          mt: '8px'
        }}
        onFileChange={(f) => (formikProps.values.file = f[0])}
        placeholder={"Archivo de " + nameString}
        clearButtonLabel="Limpiar"
        multipleFiles={false}
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        hideClearButton={true}
        ref={pickerRef}
      />
    </>
  )
}

export const FilterInput = (props: {
  attributeName: string;
  nameString: string;
  formikProps: any;
  pickerRef: any;
  productOptions: Array<any>;
}) => {
  const { attributeName, nameString, formikProps, pickerRef, productOptions } = props

  const options: CoverageFilterOption[] = buildCoverageFilterOptions(
    attributeName,
    productOptions,
  )

  return (
    <Select<CoverageFilterOption, true, GroupBase<CoverageFilterOption>>
      size="md"
      options={options}
      maxMenuHeight={160}
      placeholder={nameString}
      name="product_code"
      onChange={(event: any) => (formikProps.values.value = event.value)}
      required={true}
      ref={pickerRef}
      menuPortalTarget={
        typeof document !== "undefined" ? document.body : undefined
      }
      menuPosition="fixed"
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: 1600,
        }),
      }}
    />
  )
}

export const SetParameterModal = (props: {
  id: number;
  name: string;
  nameString: string;
  origin: string;
  originString: string;
  isOpen: boolean;
  productOptions: any;
  hasTemplate: boolean;
  reloader: (val: any) => void;
  onClose: () => void;
}) => {
  const { id, name, nameString, origin, originString, isOpen, productOptions, hasTemplate, onClose } = props;

  const ref = useRef(null);
  const { show, hide } = useLoading();
  const [error, setError] = useState(null);
  const reload = () => {
    let seed = Math.floor(Math.random() * 100);
    props.reloader(seed);
  };

  type Values = {
    value: string;
    mode: string;
    file: any;
  };
  const submitFunction = (values: any) => {
    if (origin === "user_input") {
      ApiSetCoverageInputParameterValue(id, values.value)
        .then(() => {
          reload();
          onClose();
          setError(null);
        })
        .catch((err) => {
          setError(err.response);
        });
    }
    if (origin === "mass_load") {
      show();
      ApiMassLoadCoverageParameterFile(
        name,
        values.file,
        values.mode ? values.mode : "update"
      )
        .then(() => {
          reload();
          onClose();
          hide();
          setError(null);
        })
        .catch((err) => {
          setError(err.response);
          hide();
        });
    }
  };

  return (
    <>
      <Modal
        isCentered
        isOpen={isOpen}
        onClose={() => {
          setError(null);
          onClose();
        }}
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent maxH="90vh" display="flex" flexDirection="column" mx={4}>
          <ModalHeader flexShrink={0}>{`${originString} de ${nameString}`}</ModalHeader>
          <ModalCloseButton />
          <Box
            flex="1"
            minH={0}
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
          <Formik
            onSubmit={(values) => submitFunction(values)}
            initialValues={{
              value: "",
              file: null,
              mode: "update",
            }}
          >
            {(props: FormikProps<Values>) => (
              <form
                onSubmit={props.handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <ModalBody flex="1" overflowY="auto" minH={0} py={2}>
                  {error != null ? (
                    <CustomAlert
                      status="error"
                      message={
                        (error.status === 400 || error.status === 403) && origin === "user_input"
                          ? "No se pudo actualizar la configuración."
                          : (error.status === 400) && origin === "mass_load"
                            ? error.data ? error.data.message : "Ocurrió un error procesando el archivo."
                            : "Ocurrió un error inesperado."
                      }
                      mb="24px"
                    />
                  ) : null}
                  <FormControl>
                    {origin === "user_input" ?
                      name.startsWith('filter') ?
                        (<FilterInput attributeName={name} formikProps={props} nameString={nameString} pickerRef={ref} productOptions={productOptions} />)
                        : (<CommonInput formikProps={props} nameString={nameString} />)
                      : (<MassLoadInput formikProps={props} name={name} nameString={nameString} pickerRef={ref} hasTemplate={hasTemplate} />)
                    }
                  </FormControl>
                </ModalBody>

                <ModalFooter flexShrink={0} justifyContent="center">
                  <Button type="submit">Enviar</Button>
                </ModalFooter>
              </form>
            )}
          </Formik>
          </Box>
        </ModalContent >
      </Modal >
    </>
  );
};
